import type { Game } from './Game';
import type { Vector2 } from '@wooorms/shared';
import {
  SCREEN_SHAKE_MAX_INTENSITY,
  SCREEN_SHAKE_DECAY_RATE,
  SCREEN_SHAKE_FREQUENCY,
  SCREEN_SHAKE_WARNING_THRESHOLD,
  CAMERA_ZOOM_SIZE_MIN_SEGMENTS,
  CAMERA_ZOOM_SIZE_MAX_SEGMENTS,
  CAMERA_ZOOM_MAX,
  CAMERA_ZOOM_MIN_AT_SIZE,
} from '../constants';

/**
 * Camera configuration options
 */
export interface CameraConfig {
  /** Lerp factor for smooth following (0-1, lower = smoother) */
  followLerp: number;
  /** Minimum zoom level */
  minZoom: number;
  /** Maximum zoom level */
  maxZoom: number;
  /** Initial zoom level */
  initialZoom: number;
  /** Zoom lerp factor for smooth zooming */
  zoomLerp: number;
}

/**
 * Default camera configuration
 */
const DEFAULT_CONFIG: CameraConfig = {
  followLerp: 0.1,
  minZoom: 0.3,
  maxZoom: 2.0,
  initialZoom: 1.0,
  zoomLerp: 0.1,
};

/**
 * Camera system for viewport management.
 * Handles smooth following, coordinate conversion, and zoom.
 */
export class Camera {
  /** Reference to the main game instance */
  private game: Game;

  /** Camera configuration */
  private config: CameraConfig;

  /** Current camera position in world coordinates (center of view) */
  private position: Vector2 = { x: 0, y: 0 };

  /** Target position to follow */
  private targetPosition: Vector2 = { x: 0, y: 0 };

  /** Current zoom level */
  private zoom: number;

  /** Target zoom level */
  private targetZoom: number;

  /** Screen dimensions */
  private screenWidth: number = 0;
  private screenHeight: number = 0;

  /** Screen shake current intensity (0-1) */
  private shakeIntensity: number = 0;

  /** Screen shake offset applied to camera position */
  private shakeOffset: Vector2 = { x: 0, y: 0 };

  /** Screen shake timer for oscillation */
  private shakeTimer: number = 0;

  /** Whether continuous boundary shake is active */
  private boundaryShakeActive: boolean = false;

  /** Pending timeout for start animation */
  private startAnimationTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(game: Game, config: Partial<CameraConfig> = {}) {
    this.game = game;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.zoom = this.config.initialZoom;
    this.targetZoom = this.config.initialZoom;
  }

  /**
   * Initialize the camera
   */
  init(): void {
    this.screenWidth = this.game.screenWidth;
    this.screenHeight = this.game.screenHeight;
  }

  /**
   * Handle window resize
   */
  onResize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  /**
   * Update camera position and zoom
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    // Smooth follow target position
    const lerpFactor = 1 - Math.pow(1 - this.config.followLerp, dt * 60);
    this.position.x += (this.targetPosition.x - this.position.x) * lerpFactor;
    this.position.y += (this.targetPosition.y - this.position.y) * lerpFactor;

    // Smooth zoom
    const zoomLerpFactor = 1 - Math.pow(1 - this.config.zoomLerp, dt * 60);
    this.zoom += (this.targetZoom - this.zoom) * zoomLerpFactor;

    // Update screen shake
    this.updateShake(dt);

    // Apply camera transform to world container
    this.applyTransform();
  }

  /**
   * Update screen shake effect
   * @param dt Delta time in seconds
   */
  private updateShake(dt: number): void {
    // Decay shake intensity if not in boundary shake mode
    if (!this.boundaryShakeActive && this.shakeIntensity > 0) {
      this.shakeIntensity -= SCREEN_SHAKE_DECAY_RATE * dt;
      if (this.shakeIntensity < 0) {
        this.shakeIntensity = 0;
      }
    }

    // Update shake timer
    this.shakeTimer += dt;

    // Calculate shake offset
    if (this.shakeIntensity > 0) {
      const maxOffset = SCREEN_SHAKE_MAX_INTENSITY * this.shakeIntensity;
      const frequency = SCREEN_SHAKE_FREQUENCY;

      // Use pseudo-random oscillation for more organic shake
      this.shakeOffset.x = Math.sin(this.shakeTimer * frequency) * maxOffset;
      this.shakeOffset.y = Math.cos(this.shakeTimer * frequency * 1.3) * maxOffset * 0.8;
    } else {
      this.shakeOffset.x = 0;
      this.shakeOffset.y = 0;
    }
  }

  /**
   * Apply camera transform to the world container
   */
  private applyTransform(): void {
    const worldContainer = this.game.worldContainer;

    // Set scale (zoom)
    worldContainer.scale.set(this.zoom);

    // Calculate position offset with shake
    // The world container position should center the camera position on screen
    worldContainer.position.set(
      this.screenWidth / 2 - this.position.x * this.zoom + this.shakeOffset.x,
      this.screenHeight / 2 - this.position.y * this.zoom + this.shakeOffset.y
    );
  }

  /**
   * Set the target position for the camera to follow
   * @param x World X coordinate
   * @param y World Y coordinate
   */
  setTarget(x: number, y: number): void {
    this.targetPosition.x = x;
    this.targetPosition.y = y;
  }

  /**
   * Set target from a Vector2
   */
  setTargetVector(target: Vector2): void {
    this.targetPosition.x = target.x;
    this.targetPosition.y = target.y;
  }

  /**
   * Immediately snap camera to target position
   * @param x World X coordinate
   * @param y World Y coordinate
   */
  snapTo(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
    this.targetPosition.x = x;
    this.targetPosition.y = y;
    this.applyTransform();
  }

  /**
   * Snap camera to a Vector2 position
   */
  snapToVector(target: Vector2): void {
    this.snapTo(target.x, target.y);
  }

  /**
   * Set zoom level
   * @param zoom Zoom level (clamped to min/max)
   */
  setZoom(zoom: number): void {
    this.targetZoom = Math.max(
      this.config.minZoom,
      Math.min(this.config.maxZoom, zoom)
    );
  }

  /**
   * Immediately set zoom without lerp
   */
  snapZoom(zoom: number): void {
    this.zoom = Math.max(
      this.config.minZoom,
      Math.min(this.config.maxZoom, zoom)
    );
    this.targetZoom = this.zoom;
    this.applyTransform();
  }

  /**
   * Adjust zoom by a delta amount
   */
  adjustZoom(delta: number): void {
    this.setZoom(this.targetZoom + delta);
  }

  /**
   * Update zoom based on worm size (segment count).
   * Bigger worms zoom out to see more of the map.
   * @param segmentCount Number of body segments the worm has
   */
  updateZoomForSize(segmentCount: number): void {
    // Don't scale below minimum threshold
    if (segmentCount <= CAMERA_ZOOM_SIZE_MIN_SEGMENTS) {
      this.setZoom(CAMERA_ZOOM_MAX);
      return;
    }

    // Clamp to max segments for calculation
    const clampedSegments = Math.min(segmentCount, CAMERA_ZOOM_SIZE_MAX_SEGMENTS);

    // Calculate progress from min to max segment count (0 to 1)
    const progress = (clampedSegments - CAMERA_ZOOM_SIZE_MIN_SEGMENTS) /
      (CAMERA_ZOOM_SIZE_MAX_SEGMENTS - CAMERA_ZOOM_SIZE_MIN_SEGMENTS);

    // Use sqrt for smooth scaling (zoom changes faster at first, slower later)
    const easedProgress = Math.sqrt(progress);

    // Interpolate from max zoom (1.0) to min zoom (0.65)
    const targetZoom = CAMERA_ZOOM_MAX - easedProgress * (CAMERA_ZOOM_MAX - CAMERA_ZOOM_MIN_AT_SIZE);

    this.setZoom(targetZoom);
  }

  /**
   * Play the start game zoom-in animation
   * Starts zoomed out and smoothly zooms in to normal view
   */
  playStartAnimation(): void {
    // Clear any existing animation timeout
    if (this.startAnimationTimeout) {
      clearTimeout(this.startAnimationTimeout);
    }

    // Start zoomed out (cinematic wide shot)
    this.zoom = 0.5;
    this.targetZoom = 0.5;

    // After a brief moment, begin smooth zoom to normal
    this.startAnimationTimeout = setTimeout(() => {
      this.startAnimationTimeout = null;
      this.setZoom(1.0);
    }, 100);
  }

  /**
   * Get current camera position in world coordinates
   */
  getPosition(): Vector2 {
    return { x: this.position.x, y: this.position.y };
  }

  /**
   * Get current zoom level
   */
  getZoom(): number {
    return this.zoom;
  }

  /**
   * Get target zoom level
   */
  getTargetZoom(): number {
    return this.targetZoom;
  }

  /**
   * Convert screen coordinates to world coordinates
   * @param screenX Screen X position
   * @param screenY Screen Y position
   * @returns World coordinates
   */
  screenToWorld(screenX: number, screenY: number): Vector2 {
    return {
      x: (screenX - this.screenWidth / 2) / this.zoom + this.position.x,
      y: (screenY - this.screenHeight / 2) / this.zoom + this.position.y,
    };
  }

  /**
   * Convert world coordinates to screen coordinates
   * @param worldX World X position
   * @param worldY World Y position
   * @returns Screen coordinates
   */
  worldToScreen(worldX: number, worldY: number): Vector2 {
    return {
      x: (worldX - this.position.x) * this.zoom + this.screenWidth / 2,
      y: (worldY - this.position.y) * this.zoom + this.screenHeight / 2,
    };
  }

  /**
   * Get the visible bounds in world coordinates
   */
  getVisibleBounds(): {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
  } {
    const halfWidth = this.screenWidth / 2 / this.zoom;
    const halfHeight = this.screenHeight / 2 / this.zoom;

    return {
      left: this.position.x - halfWidth,
      right: this.position.x + halfWidth,
      top: this.position.y - halfHeight,
      bottom: this.position.y + halfHeight,
      width: halfWidth * 2,
      height: halfHeight * 2,
    };
  }

  /**
   * Check if a point is visible on screen (with optional padding)
   */
  isPointVisible(x: number, y: number, padding: number = 0): boolean {
    const bounds = this.getVisibleBounds();
    return (
      x >= bounds.left - padding &&
      x <= bounds.right + padding &&
      y >= bounds.top - padding &&
      y <= bounds.bottom + padding
    );
  }

  /**
   * Check if a circle is visible on screen
   */
  isCircleVisible(x: number, y: number, radius: number): boolean {
    return this.isPointVisible(x, y, radius);
  }

  /**
   * Update follow lerp factor
   */
  setFollowLerp(lerp: number): void {
    this.config.followLerp = Math.max(0, Math.min(1, lerp));
  }

  /**
   * Trigger a one-shot screen shake effect
   * @param intensity Shake intensity from 0-1
   */
  shake(intensity: number = 1): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, Math.min(1, intensity));
    this.boundaryShakeActive = false;
  }

  /**
   * Update boundary warning shake based on warning intensity
   * Call this every frame with the current boundary warning intensity
   * @param warningIntensity Warning intensity from 0-1 (from World.getWarningIntensity)
   */
  setBoundaryWarning(warningIntensity: number): void {
    if (warningIntensity >= SCREEN_SHAKE_WARNING_THRESHOLD) {
      // Scale shake intensity based on how far past threshold we are
      const normalizedIntensity = (warningIntensity - SCREEN_SHAKE_WARNING_THRESHOLD) /
        (1 - SCREEN_SHAKE_WARNING_THRESHOLD);
      this.shakeIntensity = normalizedIntensity;
      this.boundaryShakeActive = true;
    } else {
      this.boundaryShakeActive = false;
    }
  }

  /**
   * Stop all screen shake effects
   */
  stopShake(): void {
    this.shakeIntensity = 0;
    this.boundaryShakeActive = false;
    this.shakeOffset.x = 0;
    this.shakeOffset.y = 0;
  }

  /**
   * Get current shake intensity
   */
  getShakeIntensity(): number {
    return this.shakeIntensity;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    // Clear any pending animation timeout
    if (this.startAnimationTimeout) {
      clearTimeout(this.startAnimationTimeout);
      this.startAnimationTimeout = null;
    }
  }
}
